import Head from "next/head";

import { useProjectTheme } from "lib/hooks/useProjectTheme";
import { ThemeProvider } from "lib/theme";
import { ProjectAppearance, ProjectLogo } from "server/schemas/projects";

import { PageWrapper } from "components/Page/Page";

import { ProjectAuthType } from "@prisma/client";
import { ProjectAuthWrapper } from "components/ProjectAuth/presentational";
import { ProjectPasswordAuthForm } from "components/ProjectAuth/ProjectPasswordAuthForm";
import { ConsumerAuthForm } from "components/ProjectAuth/ConsumerAuthForm/ConsumerAuthForm";
import { ProjectLogoDisplay } from "components/ProjectLogoDisplay";

type ActiveProjectAuthType = Exclude<ProjectAuthType, "None">;

type Props = {
  projectId: string;
  projectAppearance: ProjectAppearance;
  projectLogo: ProjectLogo;
  authType: ActiveProjectAuthType;
  onAuthSuccess: () => void;
  captchaEnabled: boolean;
};

export function ProjectAuthPage({
  projectId,
  projectAppearance,
  projectLogo,
  authType,
  onAuthSuccess,
  captchaEnabled,
}: Props) {
  const projectTheme = useProjectTheme({ projectAppearance });

  function renderForm() {
    switch (authType) {
      case "Password":
        return (
          <ProjectPasswordAuthForm
            projectId={projectId}
            onAuthSuccess={onAuthSuccess}
            captchaEnabled={captchaEnabled}
          />
        );
      case "Consumer":
        return (
          <ConsumerAuthForm
            projectId={projectId}
            onAuthSuccess={onAuthSuccess}
            captchaEnabled={captchaEnabled}
          />
        );

      default:
        const _exhaustiveCheck: never = authType;
        return _exhaustiveCheck;
    }
  }

  return (
    <ThemeProvider projectTheme={projectTheme}>
      <ProjectAuthWrapper>
        <Head>
          <title>Log in</title>
        </Head>

        <PageWrapper $isEditing={false}>
          <ProjectLogoDisplay logo={projectLogo} />
          {renderForm()}
        </PageWrapper>
      </ProjectAuthWrapper>
    </ThemeProvider>
  );
}
